import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

// Define the form data interface for invoice generation
interface FormData {
  companyName?: string;
  streetAddress?: string;
  postalCode?: string;
  city?: string;
  email?: string;
  registrationNumber?: string;
  [key: string]: unknown;
}

// Company sender information from the provided image
const SENDER_INFO = {
  companyName: 'Cernea Consulting AB',
  address: 'Högåsagatan 9',
  postalCode: '57133',
  city: 'Nässjö',
  country: 'Sverige',
  phone: '0760295155',
  email: 'victor@cerneaconsulting.com',
  website: 'cerneaconsulting.com',
  reference: 'Victor Cernea',
  organizationNumber: '559493-0322',
  vatNumber: 'SE559493032201',
  bankGiro: '637-5232',
  currency: 'SEK',
};

/**
 * Service to handle invoice PDF generation
 */
export const InvoiceService = {
  /**
   * Generates an invoice PDF for the company registration service
   * @param formData The registration form data (used for receiver information)
   * @returns A Uint8Array representing the invoice PDF file
   */
  async generateInvoicePdf(formData: FormData): Promise<Uint8Array> {
    try {
      // Create a new PDF document
      const pdfDoc = await PDFDocument.create();

      // Add a blank page (A4 size)
      const page = pdfDoc.addPage([595.28, 841.89]);

      // Get fonts
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Colors
      const blackColor = rgb(0, 0, 0);
      const grayColor = rgb(0.5, 0.5, 0.5);
      const blueColor = rgb(0.2, 0.4, 0.8);
      const lightGrayColor = rgb(0.9, 0.9, 0.9);

      // Page dimensions
      const pageWidth = 595.28;
      const pageHeight = 841.89;
      const margin = 50;

      let yPosition = pageHeight - margin;

      // Embed and draw company logo at top center
      try {
        const logoResponse = await fetch('/assets/black_wide_logo.jpeg');
        const logoBytes = new Uint8Array(await logoResponse.arrayBuffer());
        const logoImage = await pdfDoc.embedJpg(logoBytes);

        const headerHeight = 100;
        const headerY = pageHeight - headerHeight;

        const maxLogoWidth = 300;
        const maxLogoHeight = headerHeight - 20; // small padding inside header
        const scale = Math.min(maxLogoWidth / logoImage.width, maxLogoHeight / logoImage.height);
        const logoWidth = logoImage.width * scale;
        const logoHeight = logoImage.height * scale;

        const logoX = pageWidth / 2 - logoWidth - 50; // Move logo to the left of center
        const logoY = headerY + (headerHeight - logoHeight) / 2;

        page.drawImage(logoImage, { x: logoX, y: logoY, width: logoWidth, height: logoHeight });
      } catch (e) {
        console.warn('Logo could not be loaded for invoice header:', e);
      }

      yPosition -= 70;

      // Sender address
      page.drawText(SENDER_INFO.address, {
        x: margin,
        y: yPosition,
        size: 10,
        font: font,
        color: blackColor,
      });

      yPosition -= 15;

      page.drawText(`${SENDER_INFO.postalCode} ${SENDER_INFO.city}`, {
        x: margin,
        y: yPosition,
        size: 10,
        font: font,
        color: blackColor,
      });

      yPosition -= 15;

      page.drawText(`${SENDER_INFO.country}`, {
        x: margin,
        y: yPosition,
        size: 10,
        font: font,
        color: blackColor,
      });

      // Contact information in right column of header
      let rightColumnY = pageHeight - 70;
      const rightColumnX = pageWidth - 200;

      page.drawText('Kontaktuppgifter', {
        x: rightColumnX,
        y: rightColumnY,
        size: 12,
        font: boldFont,
        color: blackColor,
      });

      rightColumnY -= 20;

      page.drawText(`Tel: ${SENDER_INFO.phone}`, {
        x: rightColumnX,
        y: rightColumnY,
        size: 10,
        font: font,
        color: blackColor,
      });

      rightColumnY -= 15;

      page.drawText(`E-post: ${SENDER_INFO.email}`, {
        x: rightColumnX,
        y: rightColumnY,
        size: 10,
        font: font,
        color: blackColor,
      });

      rightColumnY -= 15;

      page.drawText(`Webb: ${SENDER_INFO.website}`, {
        x: rightColumnX,
        y: rightColumnY,
        size: 10,
        font: font,
        color: blackColor,
      });

      // Company details section
      yPosition = pageHeight - 150;

      page.drawText('Företagsuppgifter', {
        x: rightColumnX,
        y: yPosition,
        size: 12,
        font: boldFont,
        color: blackColor,
      });

      yPosition -= 20;

      page.drawText(`Org.nr: ${SENDER_INFO.organizationNumber}`, {
        x: rightColumnX,
        y: yPosition,
        size: 10,
        font: font,
        color: blackColor,
      });

      yPosition -= 15;

      page.drawText(`Moms.reg.nr: ${SENDER_INFO.vatNumber}`, {
        x: rightColumnX,
        y: yPosition,
        size: 10,
        font: font,
        color: blackColor,
      });

      yPosition -= 15;

      page.drawText(`Bankgiro: ${SENDER_INFO.bankGiro}`, {
        x: rightColumnX,
        y: yPosition,
        size: 10,
        font: font,
        color: blackColor,
      });

      // FAKTURA title
      yPosition = pageHeight - 200;

      page.drawText('FAKTURA', {
        x: margin,
        y: yPosition,
        size: 32,
        font: boldFont,
        color: blackColor,
      });

      // Invoice details
      yPosition -= 50;

      const invoiceNumber = `F${Date.now().toString().slice(-8)}`;
      const currentDate = new Date().toLocaleDateString('sv-SE');
      const dueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('sv-SE'); // 30 days from now

      // Invoice info in two columns
      page.drawText(`Fakturanummer: ${invoiceNumber}`, {
        x: margin,
        y: yPosition,
        size: 11,
        font: boldFont,
        color: blackColor,
      });

      page.drawText(`Referens: ${SENDER_INFO.reference}`, {
        x: rightColumnX,
        y: yPosition,
        size: 11,
        font: font,
        color: blackColor,
      });

      yPosition -= 20;

      page.drawText(`Fakturadatum: ${currentDate}`, {
        x: margin,
        y: yPosition,
        size: 11,
        font: font,
        color: blackColor,
      });

      page.drawText(`Förfallodatum: ${dueDate}`, {
        x: rightColumnX,
        y: yPosition,
        size: 11,
        font: font,
        color: blackColor,
      });

      // Customer information section
      yPosition -= 50;

      page.drawText('Faktureras till:', {
        x: margin,
        y: yPosition,
        size: 12,
        font: boldFont,
        color: blackColor,
      });

      yPosition -= 25;

      // Customer details from form data
      const customerName = formData.companyName || 'Kund';
      const customerAddress = formData.streetAddress || '';
      const customerCity =
        formData.postalCode && formData.city
          ? `${formData.postalCode} ${formData.city}`
          : formData.city || '';
      const customerEmail = formData.email || '';
      const customerOrgNr = formData.registrationNumber || '';

      page.drawText(customerName, {
        x: margin,
        y: yPosition,
        size: 11,
        font: boldFont,
        color: blackColor,
      });

      if (customerAddress) {
        yPosition -= 15;
        page.drawText(customerAddress, {
          x: margin,
          y: yPosition,
          size: 10,
          font: font,
          color: blackColor,
        });
      }

      if (customerCity) {
        yPosition -= 15;
        page.drawText(customerCity, {
          x: margin,
          y: yPosition,
          size: 10,
          font: font,
          color: blackColor,
        });
      }

      if (customerEmail) {
        yPosition -= 15;
        page.drawText(customerEmail, {
          x: margin,
          y: yPosition,
          size: 10,
          font: font,
          color: blackColor,
        });
      }

      if (customerOrgNr) {
        yPosition -= 15;
        page.drawText(`Org.nr: ${customerOrgNr}`, {
          x: margin,
          y: yPosition,
          size: 10,
          font: font,
          color: blackColor,
        });
      }

      // Invoice items section
      yPosition -= 60;

      // Table header
      const tableHeaderY = yPosition;
      const tableHeight = 25;

      page.drawRectangle({
        x: margin,
        y: tableHeaderY - tableHeight,
        width: pageWidth - 2 * margin,
        height: tableHeight,
        color: lightGrayColor,
      });

      // Table headers
      page.drawText('Beskrivning', {
        x: margin + 10,
        y: tableHeaderY - 15,
        size: 11,
        font: boldFont,
        color: blackColor,
      });

      page.drawText('Antal', {
        x: margin + 300,
        y: tableHeaderY - 15,
        size: 11,
        font: boldFont,
        color: blackColor,
      });

      page.drawText('Pris', {
        x: margin + 360,
        y: tableHeaderY - 15,
        size: 11,
        font: boldFont,
        color: blackColor,
      });

      page.drawText('Summa', {
        x: margin + 420,
        y: tableHeaderY - 15,
        size: 11,
        font: boldFont,
        color: blackColor,
      });

      // Invoice line item
      yPosition = tableHeaderY - 40;

      page.drawText('Företagsregistrering - PDF-generering och dokumenthantering', {
        x: margin + 10,
        y: yPosition,
        size: 10,
        font: font,
        color: blackColor,
      });

      page.drawText('1', {
        x: margin + 310,
        y: yPosition,
        size: 10,
        font: font,
        color: blackColor,
      });

      page.drawText('399,00', {
        x: margin + 360,
        y: yPosition,
        size: 10,
        font: font,
        color: blackColor,
      });

      page.drawText('399,00', {
        x: margin + 430,
        y: yPosition,
        size: 10,
        font: font,
        color: blackColor,
      });

      // Draw line under item
      page.drawLine({
        start: { x: margin, y: yPosition - 10 },
        end: { x: pageWidth - margin, y: yPosition - 10 },
        thickness: 0.5,
        color: grayColor,
      });

      // Totals section
      yPosition -= 40;

      const totalsLabelX = pageWidth - 230;
      const totalsValueX = pageWidth - 120;

      page.drawText('Netto:', {
        x: totalsLabelX,
        y: yPosition,
        size: 11,
        font: font,
        color: blackColor,
      });

      page.drawText('399,00 SEK', {
        x: totalsValueX,
        y: yPosition,
        size: 11,
        font: font,
        color: blackColor,
      });

      yPosition -= 20;

      page.drawText('Moms (25%):', {
        x: totalsLabelX,
        y: yPosition,
        size: 11,
        font: font,
        color: blackColor,
      });

      page.drawText('100,00 SEK', {
        x: totalsValueX,
        y: yPosition,
        size: 11,
        font: font,
        color: blackColor,
      });

      yPosition -= 20;

      // Total line
      page.drawLine({
        start: { x: totalsLabelX, y: yPosition + 14 },
        end: { x: pageWidth - margin, y: yPosition + 14 },
        thickness: 1,
        color: blackColor,
      });

      page.drawText('TOTALT:', {
        x: totalsLabelX,
        y: yPosition,
        size: 12,
        font: boldFont,
        color: blackColor,
      });

      page.drawText('499,00 SEK', {
        x: totalsValueX,
        y: yPosition,
        size: 12,
        font: boldFont,
        color: blackColor,
      });

      // Payment information
      yPosition -= 60;

      page.drawText('Betalningsinformation', {
        x: margin,
        y: yPosition,
        size: 12,
        font: boldFont,
        color: blackColor,
      });

      yPosition -= 25;

      page.drawText(`Betala till bankgiro: ${SENDER_INFO.bankGiro}`, {
        x: margin,
        y: yPosition,
        size: 11,
        font: font,
        color: blackColor,
      });

      yPosition -= 20;

      page.drawText(`Ange fakturanummer: ${invoiceNumber}`, {
        x: margin,
        y: yPosition,
        size: 11,
        font: font,
        color: blackColor,
      });

      yPosition -= 20;

      page.drawText(`Förfallodatum: ${dueDate}`, {
        x: margin,
        y: yPosition,
        size: 11,
        font: font,
        color: blackColor,
      });

      // Footer
      const footerY = 80;

      page.drawText('Tack för ert förtroende!', {
        x: margin,
        y: footerY,
        size: 11,
        font: boldFont,
        color: blueColor,
      });

      page.drawText(`${SENDER_INFO.companyName} | ${SENDER_INFO.email} | ${SENDER_INFO.phone}`, {
        x: margin,
        y: footerY - 20,
        size: 9,
        font: font,
        color: grayColor,
      });

      // Serialize the PDF
      return await pdfDoc.save();
    } catch (error) {
      console.error('Error generating invoice PDF:', error);
      throw error;
    }
  },
};
